<template>
    <div>
        <div class="grid">
            <div class="column w-5/6">
                <div class="form-group">
                    <input
                        v-model="transactionId"
                        type="text"
                        class="form-input"
                        placeholder="A1-22-33-444"
                        @input="refresh"
                        @keydown.enter="search"
                    >
                </div>
            </div>
            <div class="column w-1/6">
                <div class="form-group" style="padding-top: 7px;">
                    <button
                        class="btn btn-md is-primary w-full"
                        @click="search"
                    >
                        Find
                    </button>
                </div>
            </div>
        </div>
        <div class="grid">
            <div class="column w-full">
                <table v-if="currentTransactionDescriptor">
                    <tbody>
                        <tr v-if="isSearching">
                            <td>
                                <font-awesome-icon
                                    icon="spinner"
                                    spin
                                    pulse
                                />
                            </td>
                            <td>
                                Searching&hellip;
                            </td>
                        </tr>
                        <tr v-else-if="! isMismatch && searchResult">
                            <td><font-awesome-icon icon="file-alt" /></td>
                            <td>
                                <span v-if="currentTransactionDescriptor.salesPeriodId && currentTransactionDescriptor.teamId">
                                    <a
                                        :href="route('admin.sales-periods.teams.transactions-summary', [currentTransactionDescriptor.salesPeriodId, currentTransactionDescriptor.teamId]) + '#' + searchResult.ref"
                                        class="font-bold"
                                    >
                                        {{ getTransactionTypeLabel(searchResult.transactionType) }}
                                        {{ searchResult.ref }}
                                    </a>
                                    &mdash;
                                    <span v-if="searchResult.transactionSubtype">Sales, {{ getTransactionSubtypeLabel(searchResult.transactionSubtype) }}</span>
                                    <span v-else>Sales</span>
                                </span>
                                <span v-else-if="currentTransactionDescriptor.deliveryProjectId">
                                    <a
                                        :href="route('admin.delivery-projects.transactions-summary', currentTransactionDescriptor.deliveryProjectId) + '#' + searchResult.ref"
                                        class="font-bold"
                                    >
                                        {{ getTransactionTypeLabel(searchResult.transactionType) }}
                                        {{ searchResult.ref }}
                                    </a>
                                    &mdash;
                                    <span v-if="searchResult.transactionSubtype">Delivery, {{ getTransactionSubtypeLabel(searchResult.transactionSubtype) }}</span>
                                    <span v-else>Delivery</span>
                                </span>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-if="isMismatch">
                        <tr>
                            <td><font-awesome-icon icon="exclamation-triangle" class="text-danger" /></td>
                            <td>
                                The reference appears to be misspelled.
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else-if="transactionId && ! isSearching && ! searchResult">
                        <tr>
                            <td><font-awesome-icon icon="times-circle" /></td>
                            <td>
                                The reference could not be found.
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-if="! isMismatch && (isSearching || searchResult)">
                        <tr v-if="currentTransactionDescriptor.salesPeriodId && currentTransactionDescriptor.teamId">
                            <td><font-awesome-icon icon="check" /></td>
                            <td>
                                <a :href="route('admin.sales-periods.teams.transactions-summary', [currentTransactionDescriptor.salesPeriodId, currentTransactionDescriptor.teamId])">
                                    Summary
                                </a>
                            </td>
                        </tr>
                        <tr v-if="currentTransactionDescriptor.salesProjectId">
                            <td><font-awesome-icon icon="dollar-sign" /></td>
                            <td>
                                <a :href="route('admin.sales-projects.show', currentTransactionDescriptor.salesProjectId)">
                                    Sales Project
                                </a>
                            </td>
                        </tr>
                        <tr v-if="currentTransactionDescriptor.salesProjectId && currentTransactionDescriptor.salesPeriodId">
                            <td><font-awesome-icon icon="dollar-sign" /></td>
                            <td>
                                <a :href="route('admin.sales-projects.sales-periods.index', currentTransactionDescriptor.salesProjectId)">
                                    All sales rounds
                                </a>
                            </td>
                        </tr>
                        <tr v-if="currentTransactionDescriptor.teamId">
                            <td><font-awesome-icon icon="users" /></td>
                            <td>
                                <a :href="route('admin.teams.show', currentTransactionDescriptor.teamId)">
                                    Sales Team
                                </a>
                            </td>
                        </tr>
                        <tr v-if="currentTransactionDescriptor.deliveryProjectId">
                            <td><font-awesome-icon icon="check" /></td>
                            <td>
                                <a :href="route('admin.delivery-projects.transactions-summary', currentTransactionDescriptor.deliveryProjectId)">
                                    Summary
                                </a>
                            </td>
                        </tr>
                        <tr v-if="currentTransactionDescriptor.deliveryProjectId">
                            <td><font-awesome-icon icon="user-hard-hat" /></td>
                            <td>
                                <a :href="route('admin.delivery-projects.show', currentTransactionDescriptor.deliveryProjectId)">
                                    Delivery Project
                                </a>
                            </td>
                        </tr>
                        <tr v-if="currentTransactionDescriptor.companyId">
                            <td><font-awesome-icon icon="briefcase" /></td>
                            <td>
                                <a :href="route('admin.companies.show', currentTransactionDescriptor.companyId)">
                                    Subcontractor
                                </a>
                            </td>
                        </tr>
                        <tr v-if="! isMismatch && invoiceDiff">
                            <td><font-awesome-icon icon="info-circle" /></td>
                            <td>
                                <span v-if="invoiceDiff.diffAddressIds.length === 0">
                                    No addresses added between
                                    <span class="whitespace-no-wrap">basis {{ invoiceDiff.txRef1 }}</span>
                                    and
                                    <span class="whitespace-no-wrap">basis {{ invoiceDiff.txRef2 }}</span>.
                                </span>
                                <div v-else>
                                    <form
                                        method="post"
                                        :action="route('admin.query-info')"
                                        class="m-0 inline"
                                        target="_blank"
                                    >
                                        <input
                                            type="hidden"
                                            name="_token"
                                            :value="token"
                                            autocomplete="off"
                                        >
                                        <input
                                            type="hidden"
                                            name="address_id"
                                            :value="invoiceDiff.diffAddressIds.join(',')"
                                        >
                                        <button
                                            type="submit"
                                            class="btn btn-link p-0 inline"
                                            style="height: 16px; line-height: 16px;"
                                        >
                                            Info
                                            <sup>
                                                <font-awesome-icon icon="link" size="xs" />
                                            </sup>
                                        </button>
                                    </form>
                                    &mdash;
                                    {{ invoiceDiff.diffAddressIds.length }}
                                    <span v-if="invoiceDiff.diffAddressIds.length !== 1">addresses</span>
                                    <span v-else>address</span>
                                    added between
                                    <span class="whitespace-no-wrap">basis {{ invoiceDiff.txRef1 }}</span>
                                    and
                                    <span class="whitespace-no-wrap">basis {{ invoiceDiff.txRef2 }}</span>.
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <table v-else-if="isForced">
                    <tbody>
                        <tr>
                            <td><font-awesome-icon icon="exclamation-triangle" class="text-danger" /></td>
                            <td>
                                Please enter a valid reference.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
<script>
    const sourceSubtypeActivation = 'activation';
    const sourceSubtypeDig = 'dig';
    const sourceSubtypeInstallation = 'installation';
    const sourceTypeDeliveryProject = 'deliveryProject';
    const sourceTypeSalesperiod = 'salesPeriod';
    const subcontractorTypeCompany = 'company';
    const subcontractorTypeTeam = 'team';
    const typeUnknown = 'unknown';

    export default {
        props: {
            companyIds: {
                type: Array,
                required: true,
            },
            teamIds: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                currentTransactionDescriptor: null,
                isForced: false,
                isMismatch: false,
                isSearching: false,
                searchResult: null,
                subcontractorTypeCompany,
                subcontractorTypeTeam,
                token: axios.defaults.headers.common['X-CSRF-TOKEN'],
                transactionId: null,
                types: {
                    a: { subcontractorType: subcontractorTypeTeam, sourceType: sourceTypeSalesperiod },
                    e: { subcontractorType: subcontractorTypeCompany, sourceType: sourceTypeDeliveryProject },
                    t: { subcontractorType: subcontractorTypeCompany, sourceType: sourceTypeDeliveryProject },
                    v: { subcontractorType: subcontractorTypeCompany, sourceType: sourceTypeDeliveryProject },
                },
            };
        },

        computed: {
            invoiceDiff() {
                return this.searchResult && this.searchResult.invoiceDiff
                    ? this.searchResult.invoiceDiff
                    : null;
            },
        },

        methods: {
            getReferences(type, ids) {
                const references = {};

                references.subcontractorTransactionId = this.getSubcontractorTransactionId(ids);

                switch (type.subcontractorType) {
                    case subcontractorTypeCompany:
                        references.companyId = this.getSubcontractorId(ids);
                        references.subcontractorType = subcontractorTypeCompany;
                        references.deliveryProjectId = this.getSourceId(ids);

                        break;
                    case subcontractorTypeTeam:
                        references.subcontractorType = subcontractorTypeTeam;
                        references.salesPeriodId = this.getSourceId(ids);
                        references.salesProjectId = this.getSourceOwnerId(ids);
                        references.teamId = this.getSubcontractorId(ids);

                        break;
                    default:
                        references.subcontractorType = typeUnknown;

                        break;
                }

                return references;
            },
            getSourceId(ids) {
                return ids[2];
            },
            getSourceOwnerId(ids) {
                return ids[1];
            },
            getSubcontractorId(ids) {
                return ids[0];
            },
            getSubcontractorTransactionId(ids) {
                return ids[3];
            },
            getTransactionDescriptor() {
                const id = this.transactionId;

                const typeChar = id[0].toLowerCase();
                const ids = id.substring(1).split('-').map(n => {
                    return Number(n);
                });

                const type = this.types[typeChar];

                const references = this.getReferences(type, ids);

                return {
                    ...type,
                    ...references,
                };
            },
            getTransactionSubtypeLabel(transactionSubtype) {
                switch (transactionSubtype) {
                    case sourceSubtypeActivation:
                        return 'Operational';
                    case sourceSubtypeDig:
                        return 'Digging';
                    case sourceSubtypeInstallation:
                        return 'Installation';
                }

                return 'Other';
            },
            getTransactionTypeLabel(transactionType) {
                switch (transactionType) {
                    case 'certify':
                        return 'Approval';
                    case 'invoice':
                        return 'Invoice';
                    case 'order':
                        return 'Basis';
                }

                return '';
            },
            refresh() {
                this.transactionId = this.transactionId.trim();

                this.currentTransactionDescriptor = null;

                if (! this.validate()) return;

                this.currentTransactionDescriptor = this.getTransactionDescriptor();

                this.search();
            },
            async search() {
                this.isForced = false;
                this.isMismatch = false;
                this.isSearching = false;
                this.searchResult = null;

                if (! this.validate()) {
                    this.isForced = true;

                    return;
                }

                const transactionDescriptor = this.getTransactionDescriptor();

                if (transactionDescriptor.companyId && ! this.companyIds.includes(transactionDescriptor.companyId)) {
                    this.isMismatch = true;

                    return;
                } else if (transactionDescriptor.teamId && ! this.teamIds.includes(transactionDescriptor.teamId)) {
                    this.isMismatch = true;

                    return;
                }

                this.isSearching = true;

                const query = this.transactionId;
                let response = null;

                try {
                    response = await axios.get(route('api.subcontractor-transactions.search', {
                        subcontractor_transaction_id: transactionDescriptor.subcontractorTransactionId,
                    }));
                } catch (error) {
                    flash_error('Failed to search. Please try reloading the page.');
                }

                this.isSearching = false;

                if (! response) return;
                if (! response.data) return;

                const searchResult = response.data;

                if (! searchResult.id) return;
                if (! searchResult.ref) return;

                if (query.toLowerCase() !== searchResult.ref.toLowerCase()) {
                    this.isMismatch = true;
                }

                this.searchResult = searchResult;

                return;
            },
            validate() {
                // A1-3-99-393

                const id = this.transactionId;

                if (! id) return false;

                const type = id[0].toLowerCase();

                if (! this.types[type]) return false;

                const numberParts = id.substring(1).split('-');

                if (numberParts.length !== 4) return false;

                const areAllPartsNumbers = numberParts.every(n => {
                    return n.length > 0 && Number.isInteger(Number(n));
                });

                if (! areAllPartsNumbers) return false;

                return true;
            },
        },
    };
</script>
<style scoped>
    td {
        padding: 0 6px 18px 0;
    }

    td:first-child {
        text-align: center;
        min-width: 30px;
    }
</style>
